/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderUserContext } from '@gh/builder-shared';
import { BuilderPermission, ContactDeliveryMethod } from '@gh/builder-shared-data';
import { isEntityInvalid, PlainImmutablePageState, setEntityFields } from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';

import { each, filter, find, isUndefined, some } from 'lodash';

import {
  complementSchedulingSupplier,
  getSchedulingSupplierListItemId,
  isSameSuppliers,
  SCHEDULING_SUPPLIER_LIST_INIT,
  SchedulingSupervisorAutocomplete,
  SchedulingSupplierListDetails,
  SchedulingSupplierListItem,
  supervisorSuppliersArr,
} from '../../resources/scheduling-supplier-list.model';

@Injectable()
export class SchedulingSupplierListFormState extends PlainImmutablePageState<SchedulingSupplierListDetails> {

  existingSupplierList$ = this.select(({ suppliers }) =>
    filter(suppliers, ({ isDeleted }) => !isDeleted));
  supervisor$ = this.select(({ supervisor }) => supervisor);
  supervisorFullName$ = this.select(({ supervisor }) => supervisor ? supervisor.userName : '');
  addSupplierDisabled$ = this.select(({ supervisor }) => supervisor && !supervisor.userId);

  constructor(private userContext: BuilderUserContext) {
    super(SCHEDULING_SUPPLIER_LIST_INIT, FormMode.ViewMode);
  }

  get supervisor(): Maybe<SchedulingSupervisorAutocomplete> {
    return this.project(({ supervisor }) => supervisor);
  }

  get preselectedSuppliers(): Maybe<SchedulingSupplierListItem[]> {
    return this.project(({ suppliers }) => filter(suppliers, ({ isDeleted }) => !isDeleted));
  }

  build(supplierListDetails: SchedulingSupplierListDetails): void {
    if (this.userContext.hasPermission(BuilderPermission.Setup.Schedules.SubContractorsSuppliers.EDIT)) {
      this.setMode(FormMode.EditMode);
    }

    this.setValue(supplierListDetails);
    this.resetDirty();
  }

  reset(): void {
    this.setValue(SCHEDULING_SUPPLIER_LIST_INIT);
    this.resetDirty();
  }

  resetSuppliers(): void {
    this.setField('suppliers', this.value.originalSuppliers);
    this.resetDirty();
  }

  addSupplierList(supplierList: SchedulingSupplierListItem[]): void {
    this.startChangeDetection();
    each(supplierList, (currentSupplier: SchedulingSupplierListItem) => {
      const existingSupplier = find(this.value.suppliers, isSameSuppliers(currentSupplier));
      if (isUndefined(existingSupplier)) {
        this.setField('suppliers', supervisorSuppliersArr.append(this.value.suppliers, currentSupplier));
      } else {
        this.markSupplierAsNotDeleted(existingSupplier);
      }
    });
    this.setDirtyIfChanged();
  }

  markSupplierAsDeleted(supplier: SchedulingSupplierListItem): void {
    this.updateSupplierDeletedFlag(supplier, true);
  }

  markSupplierAsNotDeleted(supplier: SchedulingSupplierListItem): void {
    this.updateSupplierDeletedFlag(supplier, false);
  }

  updateCurrentSupplier(supplier: SchedulingSupplierListItem,
                        field: keyof SchedulingSupplierListItem,
                        value: any): void {
    const values = (field === 'sendBy')
      ? { [field]: value, supplierAddress: value === ContactDeliveryMethod.Email ? supplier.email : supplier.address }
      : { [field]: value };
    this.startChangeDetection();
    this.setField('suppliers', supervisorSuppliersArr.update(
      this.value.suppliers,
      getSchedulingSupplierListItemId(supplier),
      complementSchedulingSupplier(setEntityFields(supplier, values)),
    ));
    this.setDirtyIfChanged();
  }

  refreshCurrentSupervisor(supervisor: Maybe<SchedulingSupervisorAutocomplete>,
                           suppliers: SchedulingSupplierListItem[],
                           originalSuppliers: SchedulingSupplierListItem[]): void {
    this.setFields({
      supervisor,
      suppliers,
      originalSuppliers,
    });
    this.resetDirty();
  }

  isSupplierListInvalid(): boolean {
    return some(this.value.suppliers, isEntityInvalid);
  }

  private updateSupplierDeletedFlag(supplier: SchedulingSupplierListItem, isDeleted: boolean): void {
    this.startChangeDetection();
    this.updateCurrentSupplier(supplier, 'isDeleted', isDeleted);
    this.setDirtyIfChanged();
  }
}
